import { useState } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Mail, MapPin, Phone, Linkedin, Instagram, Facebook } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const ContactPage = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      toast({
        title: "Message sent",
        description: "We'll be in touch within 24 hours.",
      });

      setFormData({ name: "", email: "", company: "", message: "" });
    } catch (error) {
      toast({
        title: "Error",
        description: "There was a problem sending your message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-32 bg-black">
        <div className="container-wide text-center">
          <div className="max-w-4xl mx-auto">
            <Reveal trigger="onLoad" delay={0.1}>
              <p className="label bg-purple text-white px-3 py-1 inline-block mb-6">Contact</p>
            </Reveal>
            <Reveal trigger="onLoad" delay={0.2} className="mb-8 pb-4 !overflow-visible">
              <h1 className="display-md text-white">
                Get a <span className="relative inline-block pb-1 text-[#c0ff09]">
                  Free
                  <svg
                    className="absolute -bottom-1 left-0 w-full h-2 text-[#c0ff09]"
                    viewBox="0 0 100 10"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M2,8 Q50,0 98,8"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                </span> Brand Consultation Now!
              </h1>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding bg-black border-t border-white/10">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Contact Form */}
            <div>
              <Reveal className="mb-8">
                <h2 className="text-2xl lg:text-3xl font-bold text-white">
                  Tell us your business problem.
                </h2>
              </Reveal>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <Reveal width="full">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-light mb-2 text-white/80"
                      >
                        Name
                      </label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="bg-black border-white/10 focus:border-purple h-12 text-white"
                      />
                    </div>
                  </Reveal>
                  <Reveal width="full" delay={0.1}>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-light mb-2 text-white/80"
                      >
                        Email
                      </label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="bg-black border-white/10 focus:border-purple h-12 text-white"
                      />
                    </div>
                  </Reveal>
                </div>

                <Reveal width="full" delay={0.2}>
                  <div>
                    <label
                      htmlFor="company"
                      className="block text-sm font-light mb-2 text-white/80"
                    >
                      Company
                    </label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="bg-black border-white/10 focus:border-purple h-12 text-white"
                    />
                  </div>
                </Reveal>

                <Reveal width="full" delay={0.3}>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-light mb-2 text-white/80"
                    >
                      Tell us about your challenge
                    </label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      rows={6}
                      className="bg-black border-white/10 focus:border-purple resize-none text-white"
                      placeholder="What's the noise you're trying to cut through? What signal are you trying to establish?"
                    />
                  </div>
                </Reveal>

                <Reveal delay={0.4}>
                  <Button
                    type="submit"
                    variant="signal"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </Reveal>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <Reveal className="mb-8">
                <h2 className="text-2xl lg:text-3xl font-bold text-white">
                  Get in touch.
                </h2>
              </Reveal>

              <div className="space-y-8 mb-12">
                {[
                  {
                    icon: Mail,
                    label: "Email",
                    value: "nuevamediasolutions@gmail.com",
                    href: "mailto:nuevamediasolutions@gmail.com"
                  },
                  {
                    icon: () => (
                      <div className="flex items-center gap-2">
                        <Phone size={20} className="text-purple" />
                        <svg
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                          fill="currentColor"
                          className="text-[#25D366]"
                        >
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                      </div>
                    ),
                    label: "Phone & WhatsApp",
                    value: "+91 96053 91195",
                    href: "https://wa.me/919605391195"
                  },
                  {
                    icon: MapPin,
                    label: "Location",
                    value: "Kerala | Pune | Qatar"
                  }
                ].map((item, i) => (
                  <Reveal key={item.label} delay={i * 0.1}>
                    <div className="flex gap-4">
                      <item.icon className="text-purple flex-shrink-0 mt-1" size={20} />
                      <div>
                        <p className="font-light mb-1 text-white">{item.label}</p>
                        {item.href ? (
                          <a
                            href={item.href}
                            className="text-white/60 hover:text-[#c0ff09] transition-colors duration-300"
                          >
                            {item.value}
                          </a>
                        ) : (
                          <div className="text-white/60">
                            {item.value}
                            {item.desc && <><br />{item.desc}</>}
                          </div>
                        )}
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>

              {/* Social Links */}
              <div className="mb-12">
                <Reveal className="mb-4">
                  <p className="label text-white/40">FOLLOW US</p>
                </Reveal>
                <div className="flex gap-4">
                  {[
                    { icon: Instagram, href: "https://www.instagram.com/nuevamediasolutions" },
                    { icon: Linkedin, href: "https://www.linkedin.com/company/nueva-media-solutions" },
                    { icon: Facebook, href: "https://www.facebook.com/nuevamediasolutions" }
                  ].map((social, i) => (
                    <Reveal key={i} delay={0.4 + (i * 0.1)}>
                      <a
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 border border-white/10 flex items-center justify-center text-white/60 hover:text-[#c0ff09] hover:border-[#c0ff09] transition-all duration-300"
                      >
                        <social.icon size={20} />
                      </a>
                    </Reveal>
                  ))}
                </div>
              </div>

              {/* What to Expect */}
              <Reveal delay={0.6}>
                <div className="bg-white/5 border border-white/10 p-8 rounded-lg">
                  <h3 className="text-lg font-bold mb-4 text-white">
                    What to expect
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "We'll respond within 24 hours",
                      "No sales pitch, just honest conversation",
                      "We'll discuss your noise and signal",
                      "If there's a fit, we'll outline next steps",
                    ].map((item) => (
                      <li
                        key={item}
                        className="flex items-center gap-3 text-sm text-white/60"
                      >
                        <div className="w-1.5 h-1.5 bg-purple rounded-full" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
